/**
 * Built-in routes generated from /GPX by scripts/build-routes.mjs. The processed route is built at runtime
 * with the same pipeline as a GPX import (LV95 → 10 m resampling → smoothing → grades/climbs), but from
 * elevations sampled from swisstopo at build time, so it works offline.
 */
import { buildRoute } from '../pipeline';
import { closesAsLoop, resample, type PlanarPoint } from '../geometry';
import { wgs84ToLv95 } from '../lv95';
import type { ProcessedRoute } from '../model';
import { BUILTIN_ROUTES } from './index';
import type { BuiltinRouteData, BuiltinRouteDef } from './types';

export { BUILTIN_ROUTES };

/** The route a ride uses when none is chosen: the first built-in route. */
export const DEFAULT_ROUTE_ID = BUILTIN_ROUTES[0]!.id;

export function isBuiltinRouteId(id: string | null | undefined): boolean {
  return !!id && BUILTIN_ROUTES.some((r) => r.id === id);
}

export function builtinDef(id: string): BuiltinRouteDef | undefined {
  return BUILTIN_ROUTES.find((r) => r.id === id);
}

export function processBuiltin(data: BuiltinRouteData): ProcessedRoute {
  const planar: PlanarPoint[] = data.points.map(([lat, lon, ele]) => ({ ...wgs84ToLv95(lat, lon), ele }));
  const loop = closesAsLoop(planar);
  const rs = resample(planar, 10, loop);
  const route = buildRoute({
    id: data.id,
    name: data.name,
    d: rs.d,
    e: rs.e,
    n: rs.n,
    ele: rs.ele,
    step: rs.step,
    total: rs.total,
    loop,
    waypoints: data.waypoints,
    source: 'swisstopo',
    builtin: true,
  });
  route.meta.createdAt = 0;
  route.updatedAt = 0;
  return route;
}

const cache = new Map<string, Promise<ProcessedRoute>>();

export function loadBuiltinRoute(id: string): Promise<ProcessedRoute> | undefined {
  const def = builtinDef(id);
  if (!def) return undefined;
  let p = cache.get(id);
  if (!p) {
    p = def.load().then(processBuiltin);
    p.catch(() => cache.delete(id));
    cache.set(id, p);
  }
  return p;
}

export async function loadAllBuiltinRoutes(): Promise<ProcessedRoute[]> {
  const out: ProcessedRoute[] = [];
  for (const r of BUILTIN_ROUTES) {
    try {
      out.push(await loadBuiltinRoute(r.id)!);
    } catch (err) {
      console.warn('built-in route failed to load', r.id, err);
    }
  }
  return out;
}
